import { useEffect, useState } from 'react';
import { Eye, EyeSlash, Lifebuoy, LockKey, ShieldCheck, UserCircle } from '@phosphor-icons/react';
import { api, ApiError } from '../lib/api';
import { Brand } from '../components/Brand';

export function LoginPage({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    // A live session means the browser landed here by accident; going straight
    // in beats asking someone to type a password they already gave.
    void api.session().then((session) => { if (session.authenticated) void onAuthenticated(); }).catch(() => undefined);
  }, [onAuthenticated]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.login(username.trim(), password);
      await onAuthenticated();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'ورود انجام نشد. دوباره تلاش کنید.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-layout">
      <div className="login-panel">
        <div className="login-panel__inner">
          <Brand />
          <div className="login-copy">
            <h1>ورود به فینوژان</h1>
            <p>برای دیدن صورت‌های مالی و تحلیل‌ها، با حساب سازمانی خود وارد شوید.</p>
          </div>

          <form className="login-form" onSubmit={submit}>
            <label className="field" htmlFor="login-username">
              <span className="smart-field__label">نام کاربری</span>
              <span className="smart-field__control smart-field__control--boxed credential-input">
                <UserCircle size={18} aria-hidden="true" />
                <input
                  id="login-username"
                  name="username"
                  autoComplete="username"
                  // Latin-only credentials in an RTL page: without this the
                  // caret jumps and a typed username reads back reversed.
                  dir="ltr"
                  // A mobile keyboard capitalises the first letter of a plain
                  // text field and runs autocorrect over it, which turns a
                  // typed `owner` into `Owner`. iOS happens to suppress that
                  // when `autocomplete="username"` is set, so this was not the
                  // cause of the sign-in failure that prompted the change -
                  // but relying on a browser inferring it from an unrelated
                  // hint is not a guarantee. A credential field must receive
                  // what the person typed, character for character.
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                  autoFocus
                />
              </span>
              <small className="smart-field__message">
                <span>نام کاربری، یا شماره موبایل و ایمیلی که مدیر سامانه برای شما ثبت کرده است.</span>
              </small>
            </label>

            <label className="field" htmlFor="login-password">
              <span className="smart-field__label">رمز عبور</span>
              <span className="smart-field__control smart-field__control--boxed credential-input">
                <LockKey size={18} aria-hidden="true" />
                <input
                  id="login-password"
                  name="password"
                  // Note the `text` branch: revealing the password takes the
                  // field out of `type="password"`, and with it the implicit
                  // protection from autocapitalise and autocorrect. Anyone who
                  // taps the eye to check their typing and then keeps typing
                  // gets a mangled password, so the attributes have to be
                  // explicit here too.
                  type={reveal ? 'text' : 'password'}
                  autoComplete="current-password"
                  dir="ltr"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setReveal(!reveal)}
                  aria-label={reveal ? 'پنهان کردن رمز' : 'نمایش رمز'}
                >
                  {reveal ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </span>
            </label>

            {error && <p className="form-error" role="alert">{error}</p>}

            <button className="button button--primary button--wide" type="submit" disabled={busy}>
              {busy ? 'در حال ورود…' : 'ورود'}
            </button>
          </form>

          <div className="login-security">
            <button type="button" className="button button--ghost button--small" onClick={() => setHelpOpen(!helpOpen)}>
              رمز عبور را فراموش کرده‌اید؟
            </button>
            <span className="login-security__divider" aria-hidden="true">·</span>
            {/* Deliberately not a mailto. finojan is deployed per organisation
                and has no support address of its own; the placeholder that used
                to sit here opened a mail client addressed to a domain that does
                not resolve, which is worse than telling the reader who to ask. */}
            <button type="button" className="button button--ghost button--small" onClick={() => setHelpOpen(!helpOpen)}>
              <Lifebuoy size={16} /> پشتیبانی
            </button>
          </div>

          {helpOpen && (
            // No self-service reset: finojan has no verified channel to send one
            // through, and a reset link that goes to an unverified address is a
            // way in, not a convenience.
            <div className="notice notice--info">
              <ShieldCheck size={20} weight="fill" />
              <div>
                بازنشانی رمز و پشتیبانی فینوژان از طریق مدیر سامانه سازمان شما انجام می‌شود. با او تماس
                بگیرید تا رمز موقت صادر کند؛ در ورود بعدی از شما خواسته می‌شود آن را عوض کنید.
              </div>
            </div>
          )}
        </div>
      </div>

      <aside className="login-visual" aria-hidden="true">
        {/* Decorative only, which is why it carries no text of its own: a
            rendered caption would be a second, untranslatable headline sitting
            under the real one. The veil below it is what keeps the overlaid
            copy legible whatever part of the frame the crop lands on. */}
        <img src="/brand/finojan-login-prism.webp" alt="" decoding="async" />
        <div className="login-visual__veil" />
        <div className="login-visual__content">
          <h2>از دفتر کل تا تصمیم</h2>
          <p>
            فینوژان دفتر کل و تراز آزمایشی شما را می‌خواند، صورت‌های مالی را می‌سازد و
            هر عددی که نشان می‌دهد را به ردیف اصلی همان فایل برمی‌گرداند.
          </p>
        </div>
      </aside>
    </div>
  );
}
